/**
 * Utility type that excludes falsy values from type `T`.
 * If `T` is a falsy type (`false`, `""`, `0`, `null`, or `undefined`), resolves to `never`.
 * Otherwise, resolves to `T`.
 *
 * @typeParam T - The type to evaluate.
 */
type Truthy<T> = T extends false | '' | 0 | null | undefined ? never : T;

/**
 * Type guard that filters out falsy values in arrays and other collections.
 *
 * Returns `true` if the value is truthy, narrowing the type to `Truthy<T>`.
 *
 * @param value - The value to test for truthiness.
 * @returns `true` if the value is truthy, otherwise `false`.
 * @typeParam T - The type of the value being tested.
 *
 * @example
 * // Filter out falsy values from an array:
 * const ids = payload.map(x => x.id).filter(truthyPredicate);
 */
export function truthyPredicate<T>(value: T): value is Truthy<T> {
  return Boolean(value);
}
